import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { MenuApi, ScreenApi, type MenuItem } from "./api";

export function MenuEditor() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const items = useQuery({ queryKey: ["menu-items"], queryFn: MenuApi.list });
  const screens = useQuery({ queryKey: ["screens"], queryFn: ScreenApi.list });

  const remove = useMutation({
    mutationFn: MenuApi.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu-items"] });
      qc.invalidateQueries({ queryKey: ["menu"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : "Failed"),
  });
  const save = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<MenuItem> }) => MenuApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["menu-items"] });
      qc.invalidateQueries({ queryKey: ["menu"] });
    },
  });

  const tree = useMemo(() => buildTree(items.data?.items ?? []), [items.data]);

  return (
    <div className="hierarchy">
      <header>
        <h1>Menu</h1>
        <p className="muted">Re-shape the navigation. Children inherit visibility from their pointed-to screen.</p>
      </header>
      {error && <div role="alert" className="error">{error}</div>}

      <NewMenuItemForm
        items={items.data?.items ?? []}
        screens={screens.data?.items.map((s) => ({ key: s.key, title: s.title })) ?? []}
        onCreated={() => qc.invalidateQueries({ queryKey: ["menu-items"] })}
      />

      <ul className="tree">
        {tree.map((n) => (
          <Node
            key={n.id}
            node={n}
            depth={0}
            onSave={(id, body) => save.mutate({ id, body })}
            onDelete={(id) => remove.mutate(id)}
          />
        ))}
      </ul>
    </div>
  );
}

interface TreeNode extends MenuItem {
  children: TreeNode[];
}

function buildTree(items: MenuItem[]): TreeNode[] {
  const byId: Record<string, TreeNode> = {};
  for (const i of items) byId[i.id] = { ...i, children: [] };
  const roots: TreeNode[] = [];
  for (const n of Object.values(byId)) {
    const parent = n.parentId ? byId[n.parentId] : null;
    (parent ? parent.children : roots).push(n);
  }
  const sort = (ns: TreeNode[]) => {
    ns.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    ns.forEach((n) => sort(n.children));
  };
  sort(roots);
  return roots;
}

function Node({
  node,
  depth,
  onSave,
  onDelete,
}: {
  node: TreeNode;
  depth: number;
  onSave: (id: string, body: Partial<MenuItem>) => void;
  onDelete: (id: string) => void;
}) {
  const [order, setOrder] = useState(node.order ?? 0);
  return (
    <li>
      <div className="row" style={{ marginLeft: depth * 16 }}>
        <span>
          <strong>{node.label}</strong>{" "}
          <span className="muted">
            {node.screenKey ? `→ ${node.screenKey}` : "(folder)"}
          </span>
        </span>
        <span className="row-actions">
          <input
            type="number"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            onBlur={() => order !== (node.order ?? 0) && onSave(node.id, { order })}
            style={{ width: 64 }}
            title="Order within parent"
          />
          <button
            className="danger"
            onClick={() => {
              if (window.confirm(`Remove "${node.label}" from the navigation? Users can still reach the page if they know the URL. Config-level change, not a data delete.`))
                onDelete(node.id);
            }}
          >Remove</button>
        </span>
      </div>
      {node.children.length > 0 && (
        <ul>
          {node.children.map((c) => (
            <Node key={c.id} node={c} depth={depth + 1} onSave={onSave} onDelete={onDelete} />
          ))}
        </ul>
      )}
    </li>
  );
}

function NewMenuItemForm({
  items,
  screens,
  onCreated,
}: {
  items: MenuItem[];
  screens: { key: string; title: string }[];
  onCreated: () => void;
}) {
  const [id, setId] = useState("");
  const [label, setLabel] = useState("");
  const [parentId, setParentId] = useState("");
  const [screenKey, setScreenKey] = useState("");
  const [order, setOrder] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: MenuApi.create,
    onSuccess: () => {
      setId(""); setLabel(""); setParentId(""); setScreenKey(""); setOrder(0);
      onCreated();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : "Failed"),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    create.mutate({
      id,
      label,
      parentId: parentId || null,
      screenKey: screenKey || null,
      icon: null,
      order,
    });
  }

  return (
    <form className="create-role" onSubmit={submit}>
      <div className="row-3">
        <input value={id} onChange={(e) => setId(e.target.value)} placeholder="menu.id (dot.case)" required />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label" required />
        <input
          type="number"
          value={order}
          onChange={(e) => setOrder(Number(e.target.value))}
          placeholder="Order"
        />
      </div>
      <div className="row-3" style={{ marginTop: 8 }}>
        <label>
          Parent:
          <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
            <option value="">(top-level)</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.label}</option>)}
          </select>
        </label>
        <label>
          Points to screen:
          <select value={screenKey} onChange={(e) => setScreenKey(e.target.value)}>
            <option value="">(folder — no link)</option>
            {screens.map((s) => <option key={s.key} value={s.key}>{s.title}</option>)}
          </select>
        </label>
        <button type="submit" disabled={create.isPending}>
          {create.isPending ? "Adding…" : "Add menu item"}
        </button>
      </div>
      {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
    </form>
  );
}
